import { useCallback, useEffect, useRef, useState } from "react";
import { colors, sans } from "../theme.ts";

// Pull down at the top of a screen to fetch it again. Installed to the home
// screen there is no browser chrome to reload from, and the board is written
// from other directions (chats, Claude Code, the calendar poller), so the
// screen goes out of date while nothing is touched here.
//
// Never build around this: a "last updated" line, a sync indicator, a dot that
// means stale, or a refresh on focus. All of them are a screen reporting on
// how up to date it is. The gesture is asked for, it happens, it says nothing
// afterwards.
//
// `overscroll-behavior-y: none` on html and body (index.html) is load-bearing:
// it stops Chrome on Android running its own pull-to-refresh over this one.
//
// Same behaviour as V1's, copied rather than imported.

const THRESHOLD = 64;
const CEILING = 96;
const RESISTANCE = 0.45;
/** A refresh that finds nothing looks exactly like one that finds something: it takes a moment, then it is over. */
const DWELL = 500;

export function PullToRefresh({
  onRefresh, enabled, children,
}: { onRefresh: () => Promise<void>; enabled: boolean; children: React.ReactNode }) {
  const [pull, setPull] = useState(0);
  const [dragging, setDragging] = useState(false);
  const [busy, setBusy] = useState(false);
  const start = useRef<number | null>(null);
  const distance = useRef(0);
  const working = useRef(false);

  const run = useCallback(async () => {
    if (working.current) return;
    working.current = true;
    setBusy(true);
    const held = new Promise((r) => setTimeout(r, DWELL));
    await Promise.resolve().then(onRefresh).catch(() => {});
    await held;
    working.current = false;
    distance.current = 0;
    setBusy(false);
    setPull(0);
  }, [onRefresh]);

  useEffect(() => {
    if (!enabled) return;
    const give = () => { start.current = null; distance.current = 0; setPull(0); setDragging(false); };
    const down = (e: TouchEvent) => {
      if (working.current || e.touches.length !== 1 || window.scrollY > 0) { start.current = null; return; }
      start.current = e.touches[0].clientY;
      distance.current = 0;
    };
    const move = (e: TouchEvent) => {
      if (start.current === null || working.current) return;
      const dy = e.touches[0].clientY - start.current;
      // An upward drag, or the page having scrolled under us, is an ordinary scroll.
      if (dy <= 0 || window.scrollY > 0) { give(); return; }
      const d = Math.min(CEILING, dy * RESISTANCE);
      distance.current = d;
      setPull(d);
      setDragging(true);
      if (d > 3 && e.cancelable) e.preventDefault();
    };
    const up = () => {
      if (start.current === null) return;
      const reached = distance.current >= THRESHOLD;
      start.current = null;
      setDragging(false);
      if (reached) run(); else { distance.current = 0; setPull(0); }
    };
    window.addEventListener("touchstart", down, { passive: true });
    window.addEventListener("touchmove", move, { passive: false });
    window.addEventListener("touchend", up);
    window.addEventListener("touchcancel", give);
    return () => {
      window.removeEventListener("touchstart", down);
      window.removeEventListener("touchmove", move);
      window.removeEventListener("touchend", up);
      window.removeEventListener("touchcancel", give);
    };
  }, [enabled, run]);

  useEffect(() => {
    if (!enabled && !working.current) { start.current = null; distance.current = 0; setPull(0); setDragging(false); }
  }, [enabled]);

  const offset = busy ? 44 : pull;

  return (
    // No transform on the outer element: the nav and the sheets are
    // position: fixed and live outside it, so they do not slide with the pull.
    <div style={{ position: "relative" }}>
      <div
        aria-hidden
        style={{
          position: "absolute", top: 0, left: 0, right: 0, height: Math.max(offset, 0),
          display: "flex", alignItems: "center", justifyContent: "center",
          fontFamily: sans, fontSize: 12.5, background: colors.ground, color: colors.ink2,
          opacity: offset > 8 ? 1 : 0, pointerEvents: "none", overflow: "hidden",
        }}
      >{busy ? "refreshing" : offset >= THRESHOLD ? "release" : "pull to refresh"}</div>
      <div style={{
        transform: `translateY(${offset}px)`,
        transition: dragging ? "none" : "transform 260ms cubic-bezier(.22,.61,.36,1)",
        willChange: "transform",
      }}>{children}</div>
    </div>
  );
}
